// Planet Planetary Variables colormap gradients — the same matplotlib maps the
// raster tiles are rendered with (tulum convention: swc→Blues, lst→hot,
// canopy/biomass→YlGn, carbon→viridis, ndvi→RdYlGn). t = 0 is the LOW end.

export interface ColorStop {
  t: number
  rgb: number[]
}

export interface ChoroplethDef {
  label: string
  min: number
  max: number
  stops: ColorStop[]
  low: string
  high: string
}

const VIRIDIS: ColorStop[] = [
  { t: 0, rgb: [68, 1, 84] },
  { t: 0.11, rgb: [72, 40, 120] },
  { t: 0.22, rgb: [62, 73, 137] },
  { t: 0.33, rgb: [49, 104, 142] },
  { t: 0.44, rgb: [38, 130, 142] },
  { t: 0.56, rgb: [31, 158, 137] },
  { t: 0.67, rgb: [53, 183, 121] },
  { t: 0.78, rgb: [110, 206, 88] },
  { t: 0.89, rgb: [181, 222, 43] },
  { t: 1, rgb: [253, 231, 37] },
]

const YLGN: ColorStop[] = [
  { t: 0, rgb: [255, 255, 229] },
  { t: 0.14, rgb: [247, 252, 185] },
  { t: 0.29, rgb: [217, 239, 163] },
  { t: 0.43, rgb: [173, 221, 142] },
  { t: 0.57, rgb: [120, 198, 121] },
  { t: 0.71, rgb: [65, 171, 89] },
  { t: 0.86, rgb: [35, 139, 69] },
  { t: 1, rgb: [0, 109, 44] },
]

const BLUES: ColorStop[] = [
  { t: 0, rgb: [247, 251, 255] },
  { t: 0.125, rgb: [221, 234, 246] },
  { t: 0.25, rgb: [197, 218, 238] },
  { t: 0.375, rgb: [157, 201, 224] },
  { t: 0.5, rgb: [106, 173, 213] },
  { t: 0.625, rgb: [65, 145, 197] },
  { t: 0.75, rgb: [32, 112, 180] },
  { t: 0.875, rgb: [8, 80, 154] },
  { t: 1, rgb: [8, 48, 107] },
]

const HOT: ColorStop[] = [
  { t: 0, rgb: [10, 0, 0] },
  { t: 0.125, rgb: [94, 0, 0] },
  { t: 0.25, rgb: [178, 0, 0] },
  { t: 0.375, rgb: [255, 7, 0] },
  { t: 0.5, rgb: [255, 91, 0] },
  { t: 0.625, rgb: [255, 175, 0] },
  { t: 0.75, rgb: [255, 255, 6] },
  { t: 0.875, rgb: [255, 255, 132] },
  { t: 1, rgb: [255, 255, 255] },
]

const RDYLGN: ColorStop[] = [
  { t: 0, rgb: [165, 0, 38] },
  { t: 0.125, rgb: [222, 63, 46] },
  { t: 0.25, rgb: [248, 142, 82] },
  { t: 0.375, rgb: [253, 212, 129] },
  { t: 0.5, rgb: [254, 254, 189] },
  { t: 0.625, rgb: [203, 232, 129] },
  { t: 0.75, rgb: [132, 202, 102] },
  { t: 0.875, rgb: [42, 159, 84] },
  { t: 1, rgb: [0, 104, 55] },
]

export const CHOROPLETH_DEFS: Record<string, ChoroplethDef> = {
  ndvi: { label: 'Health', min: 0.25, max: 0.85, stops: RDYLGN, low: 'stressed', high: 'thriving' },
  swc: { label: 'Water', min: 5, max: 45, stops: BLUES, low: 'dry', high: 'wet' },
  lst: { label: 'Temp', min: 22, max: 40, stops: HOT, low: 'cool', high: 'hot' },
  canopyCover: { label: 'Canopy', min: 5, max: 75, stops: YLGN, low: 'open', high: 'dense' },
  canopyHeight: { label: 'Height', min: 0, max: 30, stops: YLGN, low: 'low', high: 'tall' },
  carbonDensity: { label: 'Carbon', min: 15, max: 95, stops: VIRIDIS, low: 'low', high: 'high' },
  biomass: { label: 'Biomass', min: 0, max: 1, stops: YLGN, low: 'low', high: 'high' },
}

// CSS gradient (left = low) for legend bars.
export function cssGradient(stops: ColorStop[]): string {
  const parts = stops.map((s) => `rgb(${s.rgb.join(',')}) ${Math.round(s.t * 100)}%`)
  return `linear-gradient(to right, ${parts.join(', ')})`
}

// Same colormap as the legend bar, sampled in JS so map paint can use a plain
// match expression keyed on feature code rather than an interpolate ramp.
export function rampColor(def: ChoroplethDef | null | undefined, value: number): string | null {
  if (!def || !Number.isFinite(value)) return null
  const span = def.max - def.min
  const t = span === 0 ? 0 : Math.min(1, Math.max(0, (value - def.min) / span))
  const stops = def.stops
  let hi = stops.findIndex((s) => s.t >= t)
  if (hi <= 0) return `rgb(${stops[hi === 0 ? 0 : stops.length - 1].rgb.join(',')})`
  const a = stops[hi - 1]
  const b = stops[hi]
  const k = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t)
  const rgb = a.rgb.map((c, i) => Math.round(c + (b.rgb[i] - c) * k))
  return `rgb(${rgb.join(',')})`
}
